import axios from 'axios';

const API_BASE_URL = (
  process.env.REACT_APP_FOOD_SERVICE_ENDPOINT || 'http://localhost:4000/api'
).replace(/\/+$/, '');

const getFoodServiceUrl = (path: string) => {
  const cleanBase = API_BASE_URL.replace(/\/v1$/, '');
  return `${cleanBase}${path.startsWith('/') ? path : `/${path}`}`;
};

export interface NutritionFact {
  nutrientName: string;
  value: number;
  unitName: string;
}

export interface FoodSearchResult {
  fdcId: string;
  description: string;
}

export interface FoodPortion {
  portionDescription: string;
  gramWeight: number;
}

export interface FoodDetails {
  foodItemId: string;
  description: string;
  name_ar?: string | null;
  brand_name_en?: string | null;
  brand_name_ar?: string | null;
  servingSize: number;
  servingSizeUnit: string;
  nutritionFacts: NutritionFact[];
  portions: FoodPortion[];
}

const REQUIRED_NUTRIENT_NAMES = [
  'Total lipid (fat)',
  'Protein',
  'Total Sugars',
  'Carbohydrate, by difference',
  'Energy'
] as const;

function normalizeFoodSearchResult(food: any): FoodSearchResult {
  return {
    fdcId: String(food?.id ?? food?.fdcId ?? ''),
    description: food?.description || food?.name_en || 'Food item'
  };
}

function normalizeFoodDetails(food: any, foodItemId: string): FoodDetails {
  const nutrition = food?.nutrition || {};
  const measures = Array.isArray(food?.measures) ? food.measures : [];
  const preferredMeasure =
    measures.find((measure: any) => Boolean(measure?.is_default)) ||
    measures[0];

  const nutritionFacts: NutritionFact[] = REQUIRED_NUTRIENT_NAMES.map(
    (nutrientName: string) => {
      const value =
        nutrientName === 'Total lipid (fat)'
          ? Number(nutrition.fat_g ?? 0)
          : nutrientName === 'Protein'
          ? Number(nutrition.protein_g ?? 0)
          : nutrientName === 'Total Sugars'
          ? Number(nutrition.sugar_g ?? 0)
          : nutrientName === 'Carbohydrate, by difference'
          ? Number(nutrition.carbohydrates_g ?? 0)
          : Number(nutrition.calories ?? 0);

      return {
        nutrientName,
        value: Number.isFinite(value) ? Number(value.toFixed(2)) : 0,
        unitName: nutrientName === 'Energy' ? 'kcal' : 'g'
      };
    }
  );

  const portions: FoodPortion[] = measures
    .map((measure: any) => ({
      portionDescription:
        measure?.measure_name ||
        `${measure?.quantity ?? ''} ${measure?.unit ?? 'serving'}`.trim() ||
        'Serving',
      gramWeight: Number(measure?.gram_weight ?? 0)
    }))
    .filter(
      (portion: FoodPortion) =>
        Number.isFinite(portion.gramWeight) && portion.gramWeight > 0
    );

  return {
    foodItemId,
    description: food?.description || food?.name_en || '',
    name_ar: food?.name_ar || null,
    brand_name_en: food?.brand?.name_en || null,
    brand_name_ar: food?.brand?.name_ar || null,
    servingSize: Number(preferredMeasure?.gram_weight ?? 100),
    servingSizeUnit: preferredMeasure?.unit || 'g',
    nutritionFacts,
    portions:
      portions.length > 0
        ? portions
        : [{ portionDescription: 'Serving', gramWeight: 100 }]
  };
}

export async function searchFoods(query: string): Promise<FoodSearchResult[]> {
  try {
    if (!query || query.trim().length < 2) {
      return [];
    }

    const response = await axios.get(getFoodServiceUrl('/v1/foods/search'), {
      params: {
        q: query.trim(),
        page: 1,
        limit: 10
      }
    });

    const payload = response.data?.data ?? response.data;
    const foods = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.foods)
      ? payload.foods
      : [];

    return foods.map(normalizeFoodSearchResult).slice(0, 10);
  } catch (error) {
    console.error('Error searching foods:', error);
    throw new Error('Failed to search foods. Please try again.');
  }
}

export async function getFoodDetails(fdcId: string): Promise<FoodDetails> {
  try {
    const response = await axios.get(
      getFoodServiceUrl(`/v1/foods/${encodeURIComponent(fdcId)}`)
    );

    const payload = response.data?.data ?? response.data;
    return normalizeFoodDetails(payload, fdcId);
  } catch (error) {
    console.error('Error fetching food details:', error);
    throw new Error('Failed to fetch food details. Please try again.');
  }
}
